import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Eye } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const EmailTemplatesManager = ({ tAdmin }) => {
  const [tpl, setTpl] = useState({ subject: "", html: "" });
  const [original, setOriginal] = useState({ subject: "", html: "" });
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchTpl = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/email-templates/reminder");
      const data = { subject: res.data.subject, html: res.data.html };
      setTpl(data);
      setOriginal(data);
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTpl(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/email-templates/reminder", tpl);
      const data = { subject: res.data.subject, html: res.data.html };
      setTpl(data);
      setOriginal(data);
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      toast.success(tAdmin.templates.saved);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setTpl(original);
    toast.info(tAdmin.templates.reset);
  };

  const previewHtml = tpl.html
    .replaceAll("{{name}}", "Aminata")
    .replaceAll("{{amount}}", "30,00")
    .replaceAll("{{symbol}}", "€")
    .replaceAll("{{currency}}", "EUR");

  if (loading) return <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">…</div>;

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="email-templates">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#0A192F]">{tAdmin.templates.title}</h2>
          <p className="text-sm text-[#64748B] mt-1">{tAdmin.templates.subtitle}</p>
          {meta.updated_at && (
            <p className="text-[11px] text-[#64748B] mt-2">
              {tAdmin.templates.lastEdit} : <strong>{meta.updated_by}</strong> — {new Date(meta.updated_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowPreview((s) => !s)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FDF8EE] text-[#7A6028] hover:bg-[#F3E8C8] text-sm font-medium" data-testid="tpl-preview-btn">
            <Eye size={14} /> {showPreview ? tAdmin.templates.hidePreview : tAdmin.templates.preview}
          </button>
          <button onClick={reset} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[#64748B] hover:bg-black/5 text-sm" data-testid="tpl-reset-btn">
            <RotateCcw size={14} /> {tAdmin.templates.cancel}
          </button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C5A059] text-white hover:bg-[#B08D4A] text-sm font-medium disabled:opacity-50" data-testid="tpl-save-btn">
            <Save size={14} /> {saving ? "…" : tAdmin.templates.save}
          </button>
        </div>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E8DFC9] rounded-lg p-4 mb-4 text-xs text-[#7A6028]">
        <strong>{tAdmin.templates.varsTitle} :</strong>{" "}
        <code className="bg-white px-1.5 py-0.5 rounded mx-1">{"{{name}}"}</code>
        <code className="bg-white px-1.5 py-0.5 rounded mx-1">{"{{amount}}"}</code>
        <code className="bg-white px-1.5 py-0.5 rounded mx-1">{"{{symbol}}"}</code>
        <code className="bg-white px-1.5 py-0.5 rounded mx-1">{"{{currency}}"}</code>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.templates.fSubject}</label>
          <input type="text" value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none" data-testid="tpl-subject-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{tAdmin.templates.fHtml}</label>
          <textarea value={tpl.html} onChange={(e) => setTpl({ ...tpl, html: e.target.value })} rows={18}
            className="w-full px-3 py-2 rounded-md border border-black/10 focus:border-[#C5A059] focus:outline-none font-mono text-xs leading-relaxed" data-testid="tpl-html-input" />
        </div>
      </div>

      {showPreview && (
        <div className="mt-6">
          <div className="text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">{tAdmin.templates.preview}</div>
          <div className="border border-black/10 rounded-lg overflow-hidden bg-white" data-testid="tpl-preview-iframe">
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-[500px] bg-white" sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesManager;
